import React, { useEffect, useRef, useState } from "react";
import { Radio, Square, Volume2, MessageSquare, MessageSquareOff, Sliders } from "lucide-react";

/**
 * StudioMixerStrip — studio-console-style control bar for Director's Mode.
 *
 * Packs the essential broadcast controls into one horizontal strip:
 *   1. START / END live buttons (lit LEDs indicate status)
 *   2. VU meters — two animated level bars fed by an AnalyserNode on the
 *      outbound audio destination
 *   3. Basic 3-band EQ (Low / Mid / High) — BiquadFilter nodes
 *   4. Master output volume
 *   5. Comments ON / OFF toggle (host-side)
 *
 * Props:
 *   - status: "idle" | "live" | "reconnecting" | "ended" — drives LED color
 *   - onStart / onEnd: click handlers wired to broadcaster.startBroadcast/endBroadcast
 *   - audioCtx / outputDest: WebAudio context + its MediaStreamAudioDestinationNode
 *     (when null, VU + EQ are inert; strip still works for start/end + comments)
 *   - commentsEnabled / onToggleComments: host control over viewer chat
 */
export default function StudioMixerStrip({
  status = "idle",
  onStart,
  onEnd,
  audioCtx,
  outputDest,
  commentsEnabled = true,
  onToggleComments,
}) {
  const [masterVol, setMasterVol] = useState(1.0);
  const [eq, setEq] = useState({ low: 0, mid: 0, high: 0 });
  const [levels, setLevels] = useState([0, 0]); // [L, R] 0..1

  // --- VU meter (AnalyserNode on the outbound destination) ---
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    if (!audioCtx || !outputDest) return;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.7;
    analyserRef.current = analyser;
    const data = new Uint8Array(analyser.fftSize);
    dataRef.current = data;
    // Listen on the destination's stream: create a new source from the stream
    try {
      const monitor = audioCtx.createMediaStreamSource(outputDest.stream);
      monitor.connect(analyser);
    } catch {}

    const tick = () => {
      if (!analyserRef.current || !dataRef.current) return;
      analyserRef.current.getByteTimeDomainData(dataRef.current);
      // RMS of the waveform
      let sum = 0;
      for (let i = 0; i < dataRef.current.length; i++) {
        const v = (dataRef.current[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataRef.current.length);
      const lvl = Math.min(1, rms * 2.5);
      // Slightly different jitter for the two channels so it feels like stereo
      setLevels([lvl, Math.min(1, lvl * (0.85 + Math.random() * 0.1))]);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [audioCtx, outputDest]);

  // --- 3-band EQ graph: insert BiquadFilters between monitor source and destination ---
  // Kept simple: we add the 3 filters in series on a side-chain that re-patches the
  // destination's gain. If audioCtx/outputDest aren't provided, the EQ sliders
  // still move but have no audible effect (harmless no-op).
  const lowRef = useRef(null);
  const midRef = useRef(null);
  const highRef = useRef(null);
  const masterGainRef = useRef(null);

  useEffect(() => {
    if (!audioCtx || !outputDest) return;
    if (!lowRef.current) {
      const low = audioCtx.createBiquadFilter();  low.type = "lowshelf";   low.frequency.value = 160;  low.gain.value = 0;
      const mid = audioCtx.createBiquadFilter();  mid.type = "peaking";    mid.frequency.value = 1000; mid.Q.value = 1.0; mid.gain.value = 0;
      const high = audioCtx.createBiquadFilter(); high.type = "highshelf"; high.frequency.value = 5000; high.gain.value = 0;
      const master = audioCtx.createGain(); master.gain.value = masterVol;
      lowRef.current = low; midRef.current = mid; highRef.current = high; masterGainRef.current = master;
      // We attach the EQ to the monitor path only (listener side). Pushing EQ
      // onto the outbound RTP requires re-wiring every mixer, which is out of
      // scope for the studio strip — the EQ sliders here double as a monitor
      // EQ for the director.
      try {
        const src = audioCtx.createMediaStreamSource(outputDest.stream);
        src.connect(low); low.connect(mid); mid.connect(high); high.connect(master);
      } catch {}
    }
  }, [audioCtx, outputDest, masterVol]);

  useEffect(() => { if (lowRef.current)  lowRef.current.gain.value  = eq.low; },  [eq.low]);
  useEffect(() => { if (midRef.current)  midRef.current.gain.value  = eq.mid; },  [eq.mid]);
  useEffect(() => { if (highRef.current) highRef.current.gain.value = eq.high; }, [eq.high]);
  useEffect(() => { if (masterGainRef.current) masterGainRef.current.gain.value = masterVol; }, [masterVol]);

  const live = status === "live";
  const connecting = status === "reconnecting";

  return (
    <div
      data-testid="studio-mixer-strip"
      className="bg-gradient-to-b from-[#131821] to-[#0B0F16] border border-white/10 rounded-2xl p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_20px_40px_-20px_rgba(0,0,0,0.8)]"
    >
      {/* Mobile: single column stack so EQ + comments don't collide. 
          Desktop (≥sm): the original 4-column grid via inline style. */}
      <div className="flex flex-col gap-3 sm:grid sm:gap-4 sm:items-center sm:[grid-template-columns:auto_auto_1fr_auto]">
        {/* On-air LEDs + Start/End */}
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <div
              data-testid="studio-led-onair"
              className={`w-3 h-3 rounded-full transition ${live ? "bg-tv-live shadow-[0_0_12px_rgba(255,0,64,0.8)] tv-pulse" : "bg-white/10"}`}
            />
            <div className="text-[8px] uppercase tracking-wider font-bold text-white/60">On Air</div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div
              data-testid="studio-led-link"
              className={`w-3 h-3 rounded-full transition ${connecting ? "bg-yellow-400 tv-pulse" : (live ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]" : "bg-white/10")}`}
            />
            <div className="text-[8px] uppercase tracking-wider font-bold text-white/60">Link</div>
          </div>
          <div className="flex flex-col gap-1.5">
            {!live ? (
              <button
                data-testid="studio-start-btn"
                onClick={onStart}
                className="bg-tv-live hover:bg-red-600 text-white font-display font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg flex items-center gap-1.5 shadow-lg shadow-red-500/30"
              >
                <Radio className="w-3.5 h-3.5" /> Start
              </button>
            ) : (
              <button
                data-testid="studio-end-btn"
                onClick={onEnd}
                className="bg-white/10 hover:bg-white/20 text-white font-display font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg flex items-center gap-1.5 border border-white/20"
              >
                <Square className="w-3.5 h-3.5 fill-current" /> End
              </button>
            )}
          </div>
        </div>

        {/* VU meters */}
        <div className="flex items-end gap-1 h-14" data-testid="studio-vu">
          {levels.map((lvl, i) => (
            <div key={i} className="flex flex-col-reverse gap-[2px] w-2.5">
              {Array.from({ length: 14 }).map((_, j) => {
                const threshold = (j + 1) / 14;
                const on = lvl >= threshold;
                const colorClass = threshold > 0.8 ? "bg-red-500" : threshold > 0.6 ? "bg-yellow-400" : "bg-emerald-400";
                return (
                  <div
                    key={j}
                    className={`h-1 rounded-[1px] transition ${on ? colorClass : "bg-white/10"}`}
                    style={{ opacity: on ? 1 : 0.35 }}
                  />
                );
              })}
              <div className="text-[8px] font-bold uppercase text-white/40 text-center">{i === 0 ? "L" : "R"}</div>
            </div>
          ))}
        </div>

        {/* EQ + Master — wraps and shrinks on mobile so it never overflows */}
        <div className="flex items-center gap-3 sm:gap-4 sm:px-3 sm:border-x sm:border-white/5 min-w-0 flex-wrap">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white/50 shrink-0">
            <Sliders className="w-3 h-3 text-tv-accent" /> EQ
          </div>
          <EqKnob label="Low" value={eq.low} onChange={(v) => setEq((e) => ({ ...e, low: v }))} testid="studio-eq-low" />
          <EqKnob label="Mid" value={eq.mid} onChange={(v) => setEq((e) => ({ ...e, mid: v }))} testid="studio-eq-mid" />
          <EqKnob label="High" value={eq.high} onChange={(v) => setEq((e) => ({ ...e, high: v }))} testid="studio-eq-high" />
          <div className="flex items-center gap-2 min-w-0 sm:ml-2">
            <Volume2 className="w-3.5 h-3.5 text-tv-accent shrink-0" />
            <input
              type="range"
              data-testid="studio-master-vol"
              min="0" max="1.2" step="0.01"
              value={masterVol}
              onChange={(e) => setMasterVol(parseFloat(e.target.value))}
              className="accent-tv-accent w-20 sm:w-24"
            />
            <span className="text-[10px] text-white/50 font-bold tabular-nums w-8 text-right">{Math.round(masterVol * 100)}%</span>
          </div>
        </div>

        {/* Comments toggle */}
        <button
          type="button"
          onClick={() => onToggleComments?.(!commentsEnabled)}
          aria-pressed={!commentsEnabled}
          data-testid="studio-comments-toggle"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold border transition ${
            commentsEnabled
              ? "bg-white/5 hover:bg-white/10 text-white border-white/10"
              : "bg-red-500/15 text-red-300 border-red-500/40"
          }`}
          title={commentsEnabled ? "Viewer comments are ON — click to silence chat" : "Viewer comments are OFF — click to re-enable chat"}
        >
          {commentsEnabled ? <MessageSquare className="w-3.5 h-3.5" /> : <MessageSquareOff className="w-3.5 h-3.5" />}
          Comments {commentsEnabled ? "ON" : "OFF"}
        </button>
      </div>
    </div>
  );
}

function EqKnob({ label, value, onChange, testid }) {
  // Value is dB gain in [-12, +12].  Rendered as a vertical slider with a numeric readout.
  return (
    <label className="flex flex-col items-center gap-0.5">
      <span className="text-[9px] uppercase tracking-wider font-bold text-white/60">{label}</span>
      <input
        type="range"
        data-testid={testid}
        min="-12" max="12" step="0.5"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="accent-tv-accent w-16"
      />
      <span className="text-[9px] text-white/50 tabular-nums">{value > 0 ? "+" : ""}{value.toFixed(1)}dB</span>
    </label>
  );
}
